import React, { useState, useEffect } from 'react';
import { 
  Users, UserPlus, Shield, Mail, ChevronDown, ChevronUp, Settings, Globe, 
  Trash2, Edit3, Eye, CheckCircle, XCircle, AlertCircle, Clock, 
  Search, Filter, Download, MoreVertical, UserCheck, UserX
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { masterSupabase } from '../lib/supabase-master';

const UserSettingsPage = () => {
  const { rbacTags, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTolgee, setShowTolgee] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ 
    email: '', 
    role: 'CLIENT', 
    notes: '', 
    full_name: '' 
  });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [auditLogs, setAuditLogs] = useState([]);
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);

  const isAdmin = rbacTags?.includes('isAdmin') || rbacTags?.includes('isSuperAdmin');
  const isSuperAdmin = rbacTags?.includes("isSuperAdmin");
  console.log("isSuperAdmin:", isSuperAdmin);
  console.log("isSuperAdmin:", isSuperAdmin);
  const canInvite = rbacTags?.includes('isAdmin') || rbacTags?.includes('isSuperAdmin') || rbacTags?.includes('isManager');

  useEffect(() => {
    if (canInvite) loadUsers();
    if (isAdmin) loadAuditLogs();
  }, [canInvite, isAdmin]);

  const logAuditEvent = async (action, details) => {
    try {
      await fetch('/api/audit-logs/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await masterSupabase.auth.getSession()).data.session?.access_token}`,
        },
        body: JSON.stringify({ user: user.email, action, details }),
      });
      loadAuditLogs(); // Refresh logs after adding a new one
    } catch (error) {
      console.error('Error logging audit event:', error);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/users', {
        headers: {
          'Authorization': `Bearer ${(await masterSupabase.auth.getSession()).data.session?.access_token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to load users');
      const result = await response.json();
      console.log("API Response:", result);
      console.log("API Response:", result);
      setUsers(result.users || []);
    } catch (error) {
      console.error('Error loading users:', error);
      alert('Fehler beim Laden der Benutzer: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAuditLogs = async () => {
    try {
      const response = await fetch('/api/audit-logs/', {
        headers: {
          'Authorization': `Bearer ${(await masterSupabase.auth.getSession()).data.session?.access_token}`,
        },
      });
      if (response.ok) {
        const result = await response.json();
        setAuditLogs(result || []);
      }
    } catch (error) {
      console.error('Error loading audit logs:', error);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    setInviteLoading(true);
    try {
      const { data: { session } } = await masterSupabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const response = await fetch('/api/invite-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({
          email: inviteForm.email.trim().toLowerCase(),
          role: inviteForm.role,
          notes: inviteForm.notes.trim(),
          full_name: inviteForm.full_name.trim(),
        }),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to invite user');

      await logAuditEvent('User Invited', { invited_email: inviteForm.email, role: inviteForm.role });
      setShowInviteModal(false);
      setInviteForm({ email: '', role: 'CLIENT', notes: '', full_name: '' });
      loadUsers();
      alert('Benutzer erfolgreich eingeladen!');
    } catch (error) {
      console.error('Error inviting user:', error);
      alert('Fehler beim Einladen des Benutzers: ' + error.message);
    } finally {
      setInviteLoading(false);
    }
  };

  const updateUserRole = async (userId, userEmail, oldRole, newRole) => {
    if (oldRole === newRole) return;
    try {
      const { data: { session } } = await masterSupabase.auth.getSession();
      if (!session) throw new Error('No active session');

      await fetch(`/api/users/${userId}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ role: newRole }),
      });

      await logAuditEvent('User Role Changed', { target_user: userEmail, old_role: oldRole, new_role: newRole });
      loadUsers();
    } catch (error) {
      console.error('Error updating user role:', error);
      alert('Fehler beim Aktualisieren der Benutzerrolle: ' + error.message);
    }
  };

  const toggleUserStatus = async (userId, userEmail, currentStatus) => {
    try {
      const { data: { session } } = await masterSupabase.auth.getSession();
      if (!session) throw new Error('No active session');

      await fetch(`/api/users/${userId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ is_active: !currentStatus }),
      });

      await logAuditEvent('User Status Changed', { target_user: userEmail, new_status: !currentStatus ? 'Active' : 'Inactive' });
      loadUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
      alert('Fehler beim Aktualisieren des Benutzerstatus: ' + error.message);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser({
      user_id: user.user_id,
      email: user.profiles?.email || '',
      full_name: user.profiles?.full_name || '',
      role: user.role
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const { data: { session } } = await masterSupabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const response = await fetch(`/api/users/${editingUser.user_id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${session.access_token}` 
        },
        body: JSON.stringify({
          full_name: editingUser.full_name.trim(),
          email: editingUser.email.trim().toLowerCase()
        }),
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error || 'Failed to update user');
      }

      await logAuditEvent('User Updated', { 
        target_user: editingUser.email, 
        updated_fields: ['full_name', 'email'] 
      });
      
      setShowEditModal(false);
      setEditingUser(null);
      loadUsers();
      alert('Benutzer erfolgreich aktualisiert!');
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Fehler beim Aktualisieren des Benutzers: ' + error.message);
    }
  };

  const handleDeleteUser = (user) => {
    setDeletingUser(user);
    setShowDeleteModal(true);
  };

  const confirmDeleteUser = async () => {
    try {
      const { data: { session } } = await masterSupabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const response = await fetch(`/api/users/${deletingUser.user_id}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${session.access_token}` 
        },
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error || 'Failed to delete user');
      }

      await logAuditEvent('User Deleted', { 
        target_user: deletingUser.profiles?.email,
        user_id: deletingUser.user_id
      });
      
      setShowDeleteModal(false);
      setDeletingUser(null);
      loadUsers();
      alert('Benutzer erfolgreich gelöscht!');
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Fehler beim Löschen des Benutzers: ' + error.message);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'SUPER_ADMIN': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      case 'ADMIN': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300';
      case 'MANAGER': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'CLIENT': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'VIEWER': return 'bg-gray-100 text-gray-800 dark:bg-slate-800 dark:text-gray-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-slate-800 dark:text-gray-300';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'SUPER_ADMIN': return <Shield className="w-4 h-4" />;
      case 'ADMIN': return <Settings className="w-4 h-4" />;
      case 'MANAGER': return <Users className="w-4 h-4" />;
      case 'CLIENT': return <Users className="w-4 h-4" />;
      case 'VIEWER': return <Eye className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  const getAvailableRoles = () => {
    if (isSuperAdmin) {
      return ['VIEWER', 'CLIENT', 'MANAGER', 'ADMIN', 'SUPER_ADMIN'];
    } else if (isAdmin) {
      return ['VIEWER', 'CLIENT', 'MANAGER', 'ADMIN'];
    } else {
      return ['VIEWER', 'CLIENT'];
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm || 
      user.profiles?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.profiles?.full_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'ALL' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'ALL' || 
      (statusFilter === 'ACTIVE' && user.is_active) ||
      (statusFilter === 'INACTIVE' && !user.is_active);
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Invalid Date';
    try {
      return new Date(timestamp).toLocaleString('de-DE', {
        dateStyle: 'medium',
        timeStyle: 'short'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
              Benutzereinstellungen
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Verwalten Sie Benutzer, Rollen und System-Einstellungen
            </p>
          </div>

          <div className="p-6 space-y-6">
            {canInvite && (
              <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Benutzerverwaltung</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Benutzer einladen und Rollen verwalten ({filteredUsers.length} von {users.length} Benutzern)
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      {isAdmin && (
                        <button
                          onClick={() => setShowAuditLogs(!showAuditLogs)}
                          className="inline-flex items-center px-3 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-slate-800 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-colors"
                        >
                          <Clock className="w-4 h-4 mr-2" />
                          Audit Log
                        </button>
                      )}
                      <button
                        onClick={() => setShowInviteModal(true)}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Benutzer einladen
                      </button>
                    </div>
                  </div>
                </div>

                <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800/50">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          placeholder="Nach E-Mail oder Name suchen..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <select
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="ALL">Alle Rollen</option>
                        <option value="SUPER_ADMIN">Super Admin</option>
                        <option value="ADMIN">Admin</option>
                        <option value="MANAGER">Manager</option>
                        <option value="CLIENT">Client</option>
                        <option value="VIEWER">Viewer</option>
                      </select>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="ALL">Alle Status</option>
                        <option value="ACTIVE">Aktiv</option>
                        <option value="INACTIVE">Inaktiv</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  {loading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="text-gray-600 dark:text-gray-400 mt-2">Lade Benutzer...</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-slate-800">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Benutzer</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Rolle</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Erstellt</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Aktionen</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-gray-700">
                          {filteredUsers.map((u) => (
                            <tr key={u.user_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0 h-10 w-10">
                                    <div className="h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                                      <Users className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                                    </div>
                                  </div>
                                  <div className="ml-4">
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">{u.profiles?.full_name || 'Unbekannt'}</div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">{u.profiles?.email || 'Keine E-Mail'}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <select 
                                  value={u.role} 
                                  onChange={(e) => updateUserRole(u.user_id, u.profiles?.email, u.role, e.target.value)}
                                  className={`p-1 rounded-md text-xs ${getRoleColor(u.role)}`}
                                >
                                  {getAvailableRoles().map(role => <option key={role} value={role}>{role}</option>)}
                                </select>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <button onClick={() => toggleUserStatus(u.user_id, u.profiles?.email, u.is_active)} className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${u.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                  {u.is_active ? 'Aktiv' : 'Inaktiv'}
                                </button>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{formatTimestamp(u.created_at)}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                <button 
                                  onClick={() => handleEditUser(u)}
                                  className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 p-1 rounded-full transition-colors"
                                  title="Benutzer bearbeiten"
                                >
                                  <Edit3 className="w-4 h-4" />
                                </button>
                                <button 
                                  onClick={() => handleDeleteUser(u)}
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1 rounded-full transition-colors"
                                  title="Benutzer löschen"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {isAdmin && showAuditLogs && (
              <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-gray-700 rounded-lg mt-6">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-medium">Audit Log</h3>
                </div>
                <div className="p-6">
                  <ul className="space-y-2">
                    {auditLogs.map(log => (
                      <li key={log.id} className="flex justify-between items-center p-2 bg-gray-50 rounded-md">
                        <div>
                          <span className="font-semibold">{log.action}</span> by <span className="font-medium">{log.user}</span>
                          {log.details && <span className="text-gray-500 text-sm"> - {JSON.stringify(log.details)}</span>}
                        </div>
                        <span className="text-xs text-gray-500">{formatTimestamp(log.timestamp)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {isSuperAdmin && (
              <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-gray-700 rounded-lg mt-6">
                <button onClick={() => setShowTolgee(!showTolgee)} className="w-full p-6 text-left flex justify-between items-center">
                  <h3 className="text-lg font-medium">Tolgee Übersetzungsmanager</h3>
                  <ChevronDown className={`w-5 h-5 transform transition-transform ${showTolgee ? 'rotate-180' : ''}`} />
                </button>
                {showTolgee && (
                  <div className="p-6">
                    <iframe src="http://217.154.242.134:8085" className="w-full h-96" title="Tolgee"></iframe>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-md w-full">
            <form onSubmit={handleInviteUser} className="p-6 space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Neuen Benutzer einladen</h3>
              <input 
                type="email" 
                required 
                value={inviteForm.email} 
                onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})} 
                placeholder="E-Mail" 
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
              />
              <input 
                type="text" 
                value={inviteForm.full_name} 
                onChange={(e) => setInviteForm({...inviteForm, full_name: e.target.value})} 
                placeholder="Vollständiger Name" 
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
              />
              <select 
                value={inviteForm.role} 
                onChange={(e) => setInviteForm({...inviteForm, role: e.target.value})} 
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {getAvailableRoles().map(role => <option key={role} value={role}>{role}</option>)}
              </select>
              <textarea 
                value={inviteForm.notes} 
                onChange={(e) => setInviteForm({...inviteForm, notes: e.target.value})} 
                placeholder="Notizen" 
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              ></textarea>
              <div className="flex justify-end space-x-2">
                <button 
                  type="button" 
                  onClick={() => setShowInviteModal(false)} 
                  className="px-4 py-2 bg-gray-200 dark:bg-slate-800 text-gray-900 dark:text-white rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Abbrechen
                </button>
                <button 
                  type="submit" 
                  disabled={inviteLoading} 
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300 dark:disabled:bg-blue-800 transition-colors"
                >
                  {inviteLoading ? 'Senden...' : 'Einladung senden'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-md w-full">
            <form onSubmit={handleUpdateUser} className="p-6 space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Benutzer bearbeiten</h3>
              <input 
                type="text" 
                required 
                value={editingUser.full_name} 
                onChange={(e) => setEditingUser({...editingUser, full_name: e.target.value})} 
                placeholder="Vollständiger Name" 
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
              />
              <input 
                type="email" 
                required 
                value={editingUser.email} 
                onChange={(e) => setEditingUser({...editingUser, email: e.target.value})} 
                placeholder="E-Mail" 
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
              />
              <div className="flex justify-end space-x-2">
                <button 
                  type="button" 
                  onClick={() => { setShowEditModal(false); setEditingUser(null); }} 
                  className="px-4 py-2 bg-gray-200 dark:bg-slate-800 text-gray-900 dark:text-white rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Abbrechen
                </button>
                <button 
                  type="submit" 
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  Speichern
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && deletingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Benutzer löschen</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Möchten Sie den Benutzer <strong className="text-gray-900 dark:text-white">{deletingUser.profiles?.full_name}</strong> ({deletingUser.profiles?.email}) wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
            <div className="flex justify-end space-x-2">
              <button 
                onClick={() => { setShowDeleteModal(false); setDeletingUser(null); }} 
                className="px-4 py-2 bg-gray-200 dark:bg-slate-800 text-gray-900 dark:text-white rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Abbrechen
              </button>
              <button 
                onClick={confirmDeleteUser} 
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserSettingsPage;

